import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Search, User, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { brand, navItems, announcements } from "../mock/mock";

const AnnouncementBar = () => {
  const [i, setI] = useState(0);
  const total = announcements.length;

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % total), 4500);
    return () => clearInterval(t);
  }, [total]);

  const prev = () => setI((v) => (v - 1 + total) % total);
  const next = () => setI((v) => (v + 1) % total);

  return (
    <div className="w-full bg-[#FFF6F9] border-b border-[#F3D9E2]">
      <div className="max-w-7xl mx-auto px-4 h-10 flex items-center justify-between">
        <button aria-label="Précédent" onClick={prev} className="text-[#3A2A30]/70 hover:text-[#9D184C] transition-colors">
          <ChevronLeft size={18} />
        </button>
        <span className="font-title text-[11px] md:text-sm tracking-[0.18em] text-[#3A2A30] text-center px-2 truncate">
          {announcements[i]}
        </span>
        <button aria-label="Suivant" onClick={next} className="text-[#3A2A30]/70 hover:text-[#9D184C] transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

const Logo = ({ size = "md" }) => (
  <Link to="/" className="flex items-center justify-center">
    <img
      src={brand.logo}
      alt="Rim' Pâtisse — L'art de la gourmandise"
      className={`w-auto object-contain ${size === "sm" ? "h-14" : "h-24 md:h-28"}`}
    />
  </Link>
);

const IconBtn = ({ children, label, onClick }) => (
  <button
    aria-label={label}
    onClick={onClick}
    className="w-10 h-10 flex items-center justify-center text-[#3A2A30] hover:text-[#9D184C] transition-colors"
  >
    {children}
  </button>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const comingSoon = () => toast("Bientôt disponible", { description: "Cette fonctionnalité arrive prochainement." });

  return (
    <header className={`sticky top-0 z-50 w-full transition-shadow duration-300 ${scrolled ? "shadow-[0_4px_20px_rgba(157,24,76,0.10)]" : ""}`}>
      <AnnouncementBar />

      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-3 items-center h-28 md:h-36">
            {/* Left icons */}
            <div className="flex items-center gap-1 justify-self-start">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <IconBtn label="Menu"><Menu size={22} /></IconBtn>
                </SheetTrigger>
                <SheetContent side="left" className="bg-white w-[85vw] sm:max-w-sm p-0">
                  <div className="flex items-center justify-between px-6 h-20 border-b border-[#F3D9E2]">
                    <Logo size="sm" />
                    <SheetClose asChild>
                      <button aria-label="Fermer" className="text-[#3A2A30] hover:text-[#9D184C]"><X size={22} /></button>
                    </SheetClose>
                  </div>
                  <nav className="flex flex-col px-6 py-4">
                    {navItems.map((item) => {
                      const active = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`py-3.5 font-body text-sm uppercase tracking-wide border-b border-[#F3D9E2]/70 transition-colors ${
                            active ? "text-[#9D184C]" : "text-[#3A2A30] hover:text-[#9D184C]"
                          }`}
                        >
                          {item.label}
                        </Link>
                      );
                    })}
                  </nav>
                </SheetContent>
              </Sheet>
              <IconBtn label="Recherche" onClick={() => setSearchOpen((v) => !v)}><Search size={20} /></IconBtn>
            </div>

            {/* Center logo */}
            <div className="justify-self-center">
              <Logo />
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-1 justify-self-end">
              <IconBtn label="Compte" onClick={comingSoon}><User size={20} /></IconBtn>
            </div>
          </div>

          {/* Search bar (toggle) */}
          <div className={`overflow-hidden transition-all duration-300 ${searchOpen ? "max-h-20 pb-4" : "max-h-0"}`}>
            <form
              onSubmit={(e) => { e.preventDefault(); toast("Recherche bientôt disponible"); }}
              className="flex items-center gap-2 border border-[#F3D9E2] rounded-full px-4 py-2 max-w-xl mx-auto bg-[#FFF6F9]"
            >
              <Search size={18} className="text-[#9D184C]" />
              <input
                autoFocus={searchOpen}
                placeholder="Rechercher une gourmandise…"
                className="flex-1 bg-transparent outline-none font-body text-sm text-[#3A2A30] placeholder:text-[#3A2A30]/40"
              />
            </form>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
